import { BlogPostContentType } from 'constants/blog';
import {
  BLOG_NOTION_DASHBOARD_1,
  BLOG_NOTION_DASHBOARD_2,
  BLOG_NOTION_DASHBOARD_3,
  BLOG_NOTION_DASHBOARD_4,
  BLOG_NOTION_DASHBOARD_5,
  BLOG_NOTION_DASHBOARD_6,
  BLOG_NOTION_DASHBOARD_MAIN,
  BLOG_NOTION_DASHBOARD_PREVIEW,
} from 'constants/images';
import { BlogPost } from 'containers/blog/post/types';

const NotionDashboardPost: BlogPost = {
  id: 'notion-dashboard',
  title: 'My Notion productivity system',
  subtitle: 'Keeping track of tasks and projects',
  previewImage: BLOG_NOTION_DASHBOARD_PREVIEW,
  date: new Date('22 Feb 2023'),
  tags: ['productivity', 'management', 'notion'],
  content: [
    {
      type: BlogPostContentType.TITLE,
      content: 'My Notion productivity system',
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: 'Keeping track of tasks and projects',
    },
    {
      type: BlogPostContentType.POST_INFO,
      content: '22 Feb 2023 · 6 minute read',
    },
    {
      type: BlogPostContentType.NORMAL_IMAGE,
      content: BLOG_NOTION_DASHBOARD_MAIN,
    },
    {
      type: BlogPostContentType.TEXT,
      content:
        'I often find myself having to deal with multiple projects at a time. On a typical day, I work on at least a client project, at least a personal one, and take care of other tasks such as paying bills, filing taxes, reading tech articles, and responding to emails.',
    },
    {
      type: BlogPostContentType.TEXT,
      content: `All of those tasks can seem daunting, especially if you don't have a centralized system for managing them. In the past, I used to keep my email as a to-do list, with each unread email representing a task to be completed. Unfortunately, email apps are not designed for professional planning, so I rarely achieved "inbox 0" with this method.`,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `I also tried Todoist for a while, but it didn’t have the flexibility I needed to keep me engaged. Also, forget about hand written notes in 2023; the second I would leave for a coworking space, or travel to a different country they will forever live on my desk.`,
    },
    {
      type: BlogPostContentType.SECTION_BREAK,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `I needed a custom system, fitted to my needs. As an engineer, I’m naturally drawn to building a solution myself, exactly how I envision it. Even if I’m the only user.`,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `I had used Notion in the past for note taking, so I thought I'd give it a try. I had made some simple TODO lists with Notion before, but they quickly became unmanageable.`,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `I decided to build a dashboard to save time, prioritize tasks, and never miss a deadline.`,
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: `The main task dashboard`,
    },
    {
      type: BlogPostContentType.NORMAL_IMAGE,
      content: BLOG_NOTION_DASHBOARD_1,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `I have a tag for each project I work on. For example, AMH is for any blog related tasks, such as writing posts like this one and making improvements to the admin dashboard. `,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `Each client project has it’s own tag. It’s usually the tag I also use for the issue tracking system of that software project, on platforms like Jira or Linear. I also have tags for Thesis and Admin tasks, as well as Linkedin posts (LNK).`,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `I usually collapse the view of a category, to reduce the noise. When I have a block of time scheduled for it, I open the board. The layout is pretty default, with a Not Started, In Progress and Done columns. To remove noise, I only see tasks that are due within a month, and the tasks I completed in the current day. `,
    },
    {
      type: BlogPostContentType.NORMAL_IMAGE,
      content: BLOG_NOTION_DASHBOARD_2,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `Each task has list of fields, such as due date, a flag for very important tasks and a lead. I’ll explain this in a bit, hang tight. On the main page of the task I add any relevant details, such as links, details, or draft content for linkedin posts. For tasks that take longer, I also have a list of actions, such as when emails were send or when certain actions were taken.`,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `Let’s move on and expand on what the lead field on a task means.`,
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: `The leads list`,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `Managing your network is hard. Keeping in mind when and what you discussed with people can easily become a problem. Linked to my task list there’s a dashboard with all the people I have worked with. If you’re thinking that what you saw so far is similar to a relational database, you’d be very accurate. `,
    },
    {
      type: BlogPostContentType.NORMAL_IMAGE,
      content: BLOG_NOTION_DASHBOARD_4,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `Whenever I meet a new person, I write their details down in this dashboard, such as email, context, company and the type of contact (some are clients, some investors, etc). `,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `On each contact, I keep track of every interaction with them. Whenever we have a meeting, I write a short summary here. There’s nothing more time consuming than not remembering small details from previous discussions, having to reiterate everything.`,
    },
    {
      type: BlogPostContentType.NORMAL_IMAGE,
      content: BLOG_NOTION_DASHBOARD_5,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `Looping back to tasks, each of them can have a lead. The reason is because sometimes I create tasks during meetings, and they are highly linked to that particular person. For example, I create a task for each follow-up I have to write. It’s very convenient to have the details of the task, together with the contact, and the meeting notes in one place.`,
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: `Filtered views for specific contexts`,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `When I work on a specific project, there’s no reason to be distracted by other projects I have on the main board. Using the same datasource, I have custom views for each project, where I only see filtered tickets for this purpose only. It’s about hitting that flow and staying in the focus zone as long as possible, without many distractions.`,
    },
    {
      type: BlogPostContentType.SUBTITLE,
      content: `Quick links and daily TODOs`,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `For each client project I have underway, I have quick links to the page with more details. I also keep a list of cool tools I find online, that can become useful at any point in time.`,
    },
    {
      type: BlogPostContentType.NORMAL_IMAGE,
      content: BLOG_NOTION_DASHBOARD_6,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `On the top of the page, I also keep a synced block for daily notes. Those can be simple tasks, or more complex thoughts. Everything that is not resolved by the end of the day and still relevant becomes a task.`,
    },
    {
      type: BlogPostContentType.NORMAL_IMAGE,
      content: BLOG_NOTION_DASHBOARD_3,
    },
    {
      type: BlogPostContentType.SECTION_BREAK,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `As a freelance software engineer and founder, it's vital to keep my projects organized. This enables me to concentrate longer, be more productive, and deliver top-notch results on client projects and personal goals.`,
    },
    {
      type: BlogPostContentType.TEXT,
      content: `Sharing is caring. If you need help setting up this template, just let me know below!`,
    },
    {
      type: BlogPostContentType.CONTACT_ME_FORM,
    },
  ],
};

export default NotionDashboardPost;
